import React, { useEffect, useState } from 'react';
import { 
  Home, BrainCircuit, BookOpen, PenTool, Search, 
  TrendingUp, Settings, ShieldAlert, Rocket, Target, GraduationCap,
  X, Clock, BarChart, Trophy, Flame, Star, Award, Lock, Copy, Check, Briefcase, Zap, Gift, FileText, Download, Calendar
} from 'lucide-react';

const StudentApp = () => {
  const [userName, setUserName] = useState('Student');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [progress, setProgress] = useState(0);
  const [xp, setXp] = useState(0);
  const [userLevel, setUserLevel] = useState(1);
  const [streak, setStreak] = useState(0);
  const [userId, setUserId] = useState(null); 
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [showRewards, setShowRewards] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const playSound = (type) => {
    const sounds = {
      xp: 'https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.mp3',
      level: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3',
      achievement: 'https://assets.mixkit.co/active_storage/sfx/2019/2019-preview.mp3',
      click: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3'
    };
    const audio = new Audio(sounds[type]);
    audio.volume = 0.4;
    audio.play().catch(e => {});
  };

  const REWARDS_LIST = [
    { id: 1, title: "Top 100 MI Prompšu Grāmata", cost: 500, type: "PDF", icon: <FileText className="text-blue-400" /> },
    { id: 2, title: "Personalizēta MI Konsultācija (15min)", cost: 2000, type: "LIVE", icon: <Zap className="text-gold" /> },
    { id: 3, title: "Biznesa Automatizācijas Šabloni", cost: 1000, type: "TEMPLATE", icon: <Briefcase className="text-green-400" /> },
    { id: 4, title: "Ekskluzīvs MI Video Kurss: Advancēti Aģenti", cost: 1500, type: "VIDEO", icon: <Rocket className="text-purple-400" /> }
  ];

  const ACHIEVEMENT_LIST = [
    { id: "first_step", title: "Pirmais Solis", desc: "Sākta pirmā lekcija", icon: "🚀", color: "bg-blue-500" },
    { id: "xp_100", title: "Simtnieks", desc: "Sasniegti 100 XP", icon: "⚡", color: "bg-yellow-500" },
    { id: "xp_500", title: "Pusceļā", desc: "Sasniegti 500 XP", icon: "🔥", color: "bg-orange-500" },
    { id: "level_5", title: "Māceklis", desc: "Sasniegts 5. līmenis", icon: "🎓", color: "bg-purple-500" },
    { id: "streak_3", title: "Trīsnieks", desc: "Mācies 3 dienas pēc kārtas", icon: "📅", color: "bg-green-500" }
  ];

  const PROMPT_LIBRARY = [
    { id: 1, cat: "Mārketings", title: "Satura Stratēģija", text: "Izveido mēneša satura plānu uzņēmumam, kas nodarbojas ar [Joma]. Fokusējies uz Instagram un LinkedIn. Iekļauj 3 ierakstus nedēļā ar tēmām, mērķi un ieteicamo vizuālo materiālu." },
    { id: 2, cat: "Bizness", title: "E-pasta Automatizācija", text: "Uzraksti sēriju no 3 aukstajiem e-pastiem potenciālajam klientam par [Pakalpojums]. Esi profesionāls, lakonisks un orientēts uz vērtību." },
    { id: 3, cat: "SEO", title: "Atslēgvārdu Analīze", text: "Izanalizē šo tēmu: [Tēma]. Atrodi 10 zemas konkurences atslēgvārdus un izveido 5 bloga rakstu virsrakstus, kas piesaistītu klikšķus." },
    { id: 4, cat: "Produktivitāte", title: "Dienas Plānotājs", text: "Esmu [Amats]. Mans šodienas galvenais mērķis ir [Mērķis]. Palīdzi man strukturēt dienu, izmantojot Pomodoro tehniku un prioritizējot 3 svarīgākos uzdevumus." }
  ];

  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    let resolvedUserId = null;
    if (tg) {
      tg.ready();
      tg.expand();
      if (tg.initDataUnsafe?.user?.first_name) setUserName(tg.initDataUnsafe.user.first_name);
      if (tg.initDataUnsafe?.user?.id) resolvedUserId = tg.initDataUnsafe.user.id;
    }
    if (!resolvedUserId) {
      const params = new URLSearchParams(window.location.search);
      const urlUserId = params.get('user_id');
      if (urlUserId) resolvedUserId = urlUserId;
    }
    if (resolvedUserId) {
      setUserId(resolvedUserId);
      fetch(`/api/user_progress?user_id=${resolvedUserId}`, {
        headers: { 'Authorization': tg?.initData || '', 'Content-Type': 'application/json' }
      })
        .then(res => res.json())
        .then(data => {
          if (data.xp > xp) playSound('xp');
          if (data.level > userLevel) playSound('level');
          setProgress(data.current_lesson || 0);
          setXp(data.xp || 0);
          setUserLevel(data.level || 1);
          setStreak(data.streak || 0);
          setAchievements(data.achievements || []);
          setRecommendations(data.recommendations || []);
        })
        .catch(err => console.error(err));
    }
  }, []);

  const handleCopy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    playSound('click');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fetchLeaderboard = () => {
    playSound('click');
    setShowLeaderboard(true);
    fetch('/api/leaderboard')
      .then(res => res.json())
      .then(data => setLeaderboard(data));
  };

  const courses = [
    { id: 0, title: "🏠 0. Ievads Kursā", icon: <Home size={24} />, desc: "Kursa pārskats un tavi mērķi.", fullDesc: "Sveiks! Šajā nodaļā mēs apskatīsim kursa galvenos mērķus, platformas uzbūvi un to, kā maksimāli efektīvi izmantot Lektors AI.", time: "5 min", level: "Iesācējs", homework: "Uzraksti AI Lektoram savu lielāko gaidu no šī kursa!", video: "https://www.youtube.com/embed/ad79nYk2kEg" },
    { id: 1, title: "🧠 1. MI pamati", icon: <BrainCircuit size={24} />, desc: "Uzzini, kā AI domā.", fullDesc: "Ielūkosimies dziļāk tajā, kas patiesībā ir neironu tīkli un kā mašīnas iemācās saprast cilvēku valodu.", time: "15 min", level: "Iesācējs", homework: "Atrast 3 piemērus, kur tu ikdienā saskaries ar AI algoritmiem (piem., sociālie tīkli).", video: "https://www.youtube.com/embed/5mY296-Kk-U" },
    { id: 2, title: "🎨 2. Ģeneratīvais MI", icon: <BookOpen size={24} />, desc: "ChatGPT, Gemini, Copilot.", fullDesc: "Sāksim rakstīt 'Prompt' kā profesionāļi. Mācīsimies izspiest maksimumu no lielajiem valodas modeļiem.", time: "20 min", level: "Vidējs", homework: "Izmanto ChatGPT, lai uzrakstītu pateicības vēstuli savam pirmajam skolotājam. Iesūti prompu un rezultātu." },
    { id: 3, title: "⚙️ 3. Asistenti un Aģenti", icon: <Settings size={24} />, desc: "NotebookLM un boti.", fullDesc: "Kā izveidot savu AI asistentu, kas pārzina tavus datus un palīdz ikdienas darbos.", time: "25 min", level: "Eksperts", homework: "Izveido savu 'Custom GPT' instrukciju vienam specifiskam darba uzdevumam." },
    { id: 4, title: "📝 3a. BOSS FIGHT: Skeptiķis", icon: <PenTool size={24} />, desc: "Pierādi sevi Jānim.", fullDesc: "Tev jāmēģina pārliecināt skeptisku klientu, ka MI var palīdzēt viņa biznesam.", time: "30 min", level: "Praktisks" },
    { id: 5, title: "🖼️ 4. Attēlu veidošana", icon: <Search size={24} />, desc: "Midjourney un DALL-E.", fullDesc: "Māksla un MI. Kā ģenerēt fotoreālistiskus attēlus un grafikas mārketingam.", time: "20 min", level: "Vidējs" },
    { id: 6, title: "🎥 5. Video veidošana", icon: <TrendingUp size={24} />, desc: "Runway un Sora.", fullDesc: "Kā izmantot MI, lai radītu video saturu no nulles vai animētu attēlus.", time: "25 min", level: "Sarežģīts" },
    { id: 7, title: "✍️ 6. Digitālais saturs", icon: <PenTool size={24} />, desc: "Sociālie tīkli un teksti.", fullDesc: "Automatizēsim rakstu, relīžu un postu veidošanu taviem sociālajiem kontiem.", time: "20 min", level: "Vidējs" },
    { id: 8, title: "🔍 7. SEO un optimizācija", icon: <Search size={24} />, desc: "Atslēgvārdu analīze un meklēšana.", fullDesc: "Uzzini, kā ar AI palīdzību izveidot SEO stratēģiju, kas palīdzēs tavai mājaslapai nokļūt Google pirmajās pozīcijās.", time: "20 min", level: "Sarežģīts" },
    { id: 9, title: "📋 7a. BOSS FIGHT: Direktors", icon: <PenTool size={24} />, desc: "Prezentē satura plānu.", fullDesc: "Izveido mēneša satura stratēģiju un pārliecini direktoru par tā ROI.", time: "35 min", level: "Praktisks" },
    { id: 10, title: "📈 8. Reklāmu platformas", icon: <TrendingUp size={24} />, desc: "Meta un Google kampaņas.", fullDesc: "Mākslīgais intelekts digitālajā reklāmā. Kā uzticēt algoritmiem atrast tavu ideālo pircēju.", time: "25 min", level: "Eksperts" },
    { id: 11, title: "🎯 9. Kampaņas plānošana", icon: <Target size={24} />, desc: "Stratēģiskais grupu darbs.", fullDesc: "Plānojam lielu mārketinga kampaņu komandā, izmantojot AI aģentus.", time: "40 min", level: "Sarežģīts" },
    { id: 12, title: "📊 10. Datu vākšana", icon: <BarChart size={24} />, desc: "Analītika un Chatboti.", fullDesc: "Kā apkopot datus no klientiem un tos interpretēt ar MI palīdzību.", time: "30 min", level: "Eksperts" },
    { id: 13, title: "🛠️ 10a. BOSS FIGHT: Investors", icon: <PenTool size={24} />, desc: "Aizstāvi savu ideju.", fullDesc: "Prezentē tehnisko specifikāciju investors, lai iegūtu finansējumu savam AI aģentam.", time: "45 min", level: "Praktisks" },
    { id: 14, title: "🛡️ 11. Ētika un GDPR", icon: <ShieldAlert size={24} />, desc: "Kiberdrošība un dati.", fullDesc: "Kādus datus nedrīkst dot AI? Kā pasargāt uzņēmuma komercnoslēpumus.", time: "20 min", level: "Kritisks" },
    { id: 15, title: "🚀 12. Praktiskā lietošana I", icon: <Rocket size={24} />, desc: "Reāli biznesa gadījumi.", fullDesc: "Sākam strādāt pie reāliem uzdevumiem, risinot biznesa problēmas ar AI.", time: "40 min", level: "Eksperts" },
    { id: 16, title: "🚀 13. Praktiskā lietošana II", icon: <Rocket size={24} />, desc: "Projektu pabeigšana.", fullDesc: "Tava projekta fināla fāze - sagatavošana aizstāvēšanai.", time: "40 min", level: "Eksperts" },
    { id: 17, title: "💎 14. MI Rīku apkopojums", icon: <BookOpen size={24} />, desc: "Tava rīku kaste.", fullDesc: "Labāko rīku katalogs, kas tev paliks arī pēc kursa beigām.", time: "15 min", level: "Resurss" },
    { id: 18, title: "🎓 15. Noslēguma Lekcija", icon: <GraduationCap size={24} />, desc: "Sertifikācija.", fullDesc: "Tavs fināla pārbaudījums un kursa noslēgums. Apsveicam!", time: "60 min", level: "Meistars" }
  ];

  const handleStartLesson = async (courseId) => {
    const tg = window.Telegram?.WebApp;
    if (userId) {
      fetch('/api/start_lesson', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': tg?.initData || '' },
        body: JSON.stringify({ user_id: userId, lesson_id: courseId })
      }).catch(err => console.log(err));
      if (tg) tg.close();
      else setSelectedCourse(null);
    } else {
      alert('Kļūda: lietotājs nav identificēts.');
    }
  };

  const progressPercentage = Math.min(Math.round((progress / 10) * 100), 100);

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6 font-sans pb-12 relative overflow-hidden">
      <div className="fixed top-[-10%] left-[-10%] w-[50%] h-[50%] bg-gold/5 rounded-full blur-[150px] pointer-events-none z-0" />
      <div className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none z-0" />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-10 pb-6 border-b border-white/10 mt-4 relative">
          <div>
             <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gold to-yellow-200 tracking-wider">VIRTUĀLĀ KLASE</h1>
             <div className="flex items-center gap-3 mt-3">
                <p className="text-gray-400 text-sm">Sveiks, <span className="text-white font-bold">{userName}</span>!</p>
                <div className="flex items-center gap-1 bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full border border-orange-500/30 text-[10px] font-bold">
                    <Flame size={12} className="fill-current" /> {streak} DIENAS
                </div>
             </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => { playSound('click'); setShowRewards(true); }} className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center border border-white/10 transition-all group relative">
                <Gift className="text-pink-400" size={18} />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-pink-500 rounded-full animate-ping"></span>
            </button>
            <button onClick={() => { playSound('click'); setShowPrompts(true); }} className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center border border-white/10 transition-all group">
                <Zap className="text-yellow-400" size={18} />
            </button>
            <button onClick={() => { playSound('click'); setShowAchievements(true); }} className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center border border-white/10 transition-all group">
                <Award className="text-indigo-400" size={18} />
            </button>
            <button onClick={fetchLeaderboard} className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-xl flex items-center justify-center border border-white/10 transition-all group">
                <Trophy className="text-gold" size={18} />
            </button>
            <div className="px-3 h-10 bg-black/40 backdrop-blur-xl rounded-xl flex items-center gap-2 border border-gold/50 shadow-lg">
               <BrainCircuit className="text-gold" size={18} />
               <span className="text-xs font-bold text-white italic">{xp} XP</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-black/40 backdrop-blur-2xl rounded-3xl p-6 md:p-8 mb-10 border border-white/10 shadow-2xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
          <div className="flex justify-between text-sm mb-5 relative z-10">
            <span className="text-gray-300 uppercase tracking-widest font-bold text-xs">Mācību Progress</span>
            <span className="text-gold font-bold bg-gold/10 px-3 py-1 rounded-full border border-gold/20 shadow-inner">{progressPercentage}% (Lvl {userLevel})</span>
          </div>
          <div className="w-full bg-white/5 rounded-full h-3 shadow-inner border border-white/5 relative z-10 overflow-hidden">
            <div className="bg-gradient-to-r from-yellow-500 to-gold h-full rounded-full transition-all duration-1000 ease-out shadow-[0_0_15px_rgba(212,175,55,0.8)]" style={{ width: `${progressPercentage}%` }} />
          </div>
        </div>

        {/* Course Grid */}
        <h2 className="text-2xl font-bold mb-6 text-gray-200 tracking-wide flex items-center gap-3">
            <Target size={24} className="text-gold" /> Studiju Programma
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {courses.map((course) => {
            const isUnlocked = progress >= course.id;
            const isRecommended = recommendations.includes(course.id);
            return (
              <div 
                key={course.id} 
                onClick={() => setSelectedCourse(course)}
                className={`p-6 rounded-[30px] border transition-all duration-500 cursor-pointer flex flex-col items-start relative overflow-hidden group active:scale-95
                  ${isUnlocked ? 'bg-black/60 backdrop-blur-xl border-white/10 hover:border-gold hover:shadow-2xl' : 'bg-black/20 backdrop-blur-md border-white/5 opacity-50 grayscale'}
                  ${isRecommended && isUnlocked ? 'border-gold/50 shadow-[0_0_20px_rgba(212,175,55,0.2)]' : ''}`}
              >
                {isUnlocked && <div className="absolute -inset-1 bg-gradient-to-r from-gold/0 via-gold/10 to-gold/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl"></div>}
                <div className="relative z-10 w-full text-left">
                    <div className="flex justify-between items-start mb-6">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border shadow-inner transition-all group-hover:scale-110 ${isUnlocked ? 'bg-gradient-to-br from-gray-900 to-black text-gold border-gold/30' : 'bg-gray-900 text-gray-500 border-white/5'}`}>
                            {React.cloneElement(course.icon, { size: 24 })}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                            {isUnlocked ? <span className="bg-green-500/10 text-green-400 text-[9px] uppercase font-bold px-2 py-1 rounded-full border border-green-500/20">Pieejams</span> : <Lock size={14} className="text-white/20" />}
                            {isRecommended && <span className="bg-gold/20 text-gold text-[8px] uppercase font-black px-2 py-0.5 rounded-full border border-gold/30 animate-pulse">Tavs Fokuss</span>}
                        </div>
                    </div>
                    <h3 className="font-bold text-lg mb-2 text-white group-hover:text-gold transition-colors">{course.title}</h3>
                    <p className="text-xs text-gray-400 leading-relaxed">{course.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* MODAL: Rewards (XP Shop) */}
        {showRewards && (
          <div className="fixed inset-0 z-[70] flex justify-center items-center bg-black/90 backdrop-blur-3xl p-4 transition-all">
            <div className="bg-white/5 w-full max-w-xl rounded-[40px] border border-white/10 shadow-2xl p-8 relative flex flex-col">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-2xl font-black text-white italic flex items-center gap-3">
                        <Gift className="text-pink-400" size={28} /> RESURSU KRĀTUVE
                    </h2>
                    <button onClick={() => setShowRewards(false)} className="p-2 hover:bg-white/10 rounded-full text-gray-400 transition-all"><X size={24} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {REWARDS_LIST.map((r) => {
                        const isUnlocked = xp >= r.cost;
                        return (
                            <div key={r.id} className={`p-5 rounded-3xl border transition-all flex flex-col items-start ${isUnlocked ? 'bg-white/10 border-white/20' : 'bg-white/5 border-transparent opacity-40 grayscale'}`}>
                                <div className="flex justify-between w-full mb-4">
                                    <div className="w-10 h-10 bg-black/40 rounded-xl flex items-center justify-center border border-white/10">{r.icon}</div>
                                    <span className={`text-[10px] font-black px-2 py-1 rounded-full border ${isUnlocked ? 'bg-green-500/20 text-green-400 border-green-500/20' : 'bg-gray-500/20 text-gray-400 border-gray-500/20'}`}>
                                        {r.cost} XP
                                    </span>
                                </div>
                                <h4 className="font-bold text-sm text-white mb-2 leading-snug">{r.title}</h4>
                                <button 
                                    disabled={!isUnlocked}
                                    className={`w-full py-2 rounded-xl text-[10px] font-bold transition-all flex items-center justify-center gap-2 ${isUnlocked ? 'bg-gold text-black hover:bg-yellow-400' : 'bg-white/5 text-gray-500'}`}
                                >
                                    {isUnlocked ? <Download size={14}/> : <Lock size={14}/>}
                                    {isUnlocked ? 'IEGŪT TAGAD' : 'NEPIETIEK XP'}
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>
          </div>
        )}

        {/* MODAL: Prompts */}
        {showPrompts && (
          <div className="fixed inset-0 z-[70] flex justify-center items-center bg-black/90 backdrop-blur-3xl p-4 transition-all">
            <div className="bg-white/5 w-full max-w-xl rounded-[40px] border border-white/10 shadow-2xl p-8 relative max-h-[80vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center mb-8">
                    <h2 className="text-2xl font-black text-white italic flex items-center gap-3">
                        <Zap className="text-yellow-400" size={28} /> PROMPŠU BIBLIOTĒKA
                    </h2>
                    <button onClick={() => setShowPrompts(false)} className="p-2 hover:bg-white/10 rounded-full text-gray-400 transition-all"><X size={24} /></button>
                </div>
                <div className="space-y-4 overflow-y-auto pr-2 custom-scrollbar">
                    {PROMPT_LIBRARY.map((p) => (
                        <div key={p.id} className="p-5 rounded-3xl bg-white/5 border border-white/5 hover:border-white/20 transition-all group relative">
                            <div className="flex justify-between items-start mb-3">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-400 px-2 py-1 bg-indigo-500/10 rounded-lg">{p.cat}</span>
                                <button onClick={() => handleCopy(p.id, p.text)} className="p-2 bg-white/5 hover:bg-white/20 rounded-xl transition-all relative overflow-hidden">
                                    {copiedId === p.id ? <Check size={16} className="text-green-400" /> : <Copy size={16} className="text-gray-400" />}
                                </button>
                            </div>
                            <h4 className="font-bold text-white mb-2">{p.title}</h4>
                            <p className="text-xs text-gray-400 italic line-clamp-3 group-hover:line-clamp-none transition-all duration-500">{p.text}</p>
                        </div>
                    ))}
                </div>
            </div>
          </div>
        )}

        {/* MODAL: Leaderboard */}
        {showLeaderboard && (
          <div className="fixed inset-0 z-[60] flex justify-center items-center bg-black/80 backdrop-blur-2xl p-4 transition-all">
            <div className="bg-white/5 w-full max-w-md rounded-[40px] border border-white/10 shadow-2xl p-8 relative overflow-hidden">
                <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-gold/10 rounded-full blur-[80px] pointer-events-none" />
                <div className="flex justify-between items-center mb-8 relative z-10">
                    <h2 className="text-2xl font-black text-white italic flex items-center gap-3"><Trophy className="text-gold" size={28} /> LĪDERU TOPS</h2>
                    <button onClick={() => setShowLeaderboard(false)} className="p-2 hover:bg-white/10 rounded-full text-gray-400 transition-all"><X size={24} /></button>
                </div>
                <div className="space-y-3 relative z-10 overflow-y-auto max-h-[60vh] pr-2 custom-scrollbar">
                    {leaderboard.map((user, index) => (
                        <div key={index} className={`flex items-center gap-4 p-4 rounded-3xl border transition-all ${index === 0 ? 'bg-gold/20 border-gold/40' : 'bg-white/5 border-white/5'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-xs ${index === 0 ? 'bg-gold text-black' : 'bg-white/10 text-gray-400'}`}>{index + 1}</div>
                            <div className="flex-1">
                                <h4 className="font-bold text-sm text-white truncate flex items-center gap-2">
                                    {user.first_name || user.username}
                                    <div className="flex gap-0.5">
                                        {user.achievements && user.achievements.slice(0, 3).map(achId => (
                                            <span key={achId} className="text-[10px]">{ACHIEVEMENT_LIST.find(a => a.id === achId)?.icon}</span>
                                        ))}
                                    </div>
                                </h4>
                                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">LVL {user.level || 1}</p>
                            </div>
                            <div className="text-right text-gold font-black text-sm">{user.xp} XP</div>
                        </div>
                    ))}
                </div>
            </div>
          </div>
        )}

        {/* MODAL: Achievements */}
        {showAchievements && (
          <div className="fixed inset-0 z-[60] flex justify-center items-center bg-black/80 backdrop-blur-2xl p-4 transition-all">
            <div className="bg-white/5 w-full max-w-md rounded-[40px] border border-white/10 shadow-2xl p-8 relative overflow-hidden">
                <div className="flex justify-between items-center mb-8 relative z-10">
                    <h2 className="text-2xl font-black text-white italic flex items-center gap-3"><Award className="text-indigo-400" size={28} /> SASNIEGUMI</h2>
                    <button onClick={() => setShowAchievements(false)} className="p-2 hover:bg-white/10 rounded-full text-gray-400 transition-all"><X size={24} /></button>
                </div>
                <div className="grid grid-cols-1 gap-3 relative z-10 overflow-y-auto max-h-[60vh] pr-2 custom-scrollbar">
                    {ACHIEVEMENT_LIST.map((ach) => {
                        const isUnlocked = achievements.includes(ach.id);
                        return (
                            <div key={ach.id} className={`flex items-center gap-4 p-4 rounded-3xl border transition-all ${isUnlocked ? 'bg-white/10 border-white/20' : 'bg-white/5 border-transparent opacity-40'}`}>
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-2xl ${isUnlocked ? ach.color : 'bg-gray-800'}`}>{isUnlocked ? ach.icon : <Lock size={20} className="text-gray-600" />}</div>
                                <div className="flex-1">
                                    <h4 className="font-bold text-sm text-white">{ach.title}</h4>
                                    <p className="text-[10px] text-gray-400 font-medium">{ach.desc}</p>
                                </div>
                                {isUnlocked && <Star size={16} className="text-gold fill-current" />}
                            </div>
                        );
                    })}
                </div>
            </div>
          </div>
        )}

        {/* MODAL: Lesson Details */}
        {selectedCourse && (
          <div className="fixed inset-0 z-50 flex justify-center items-end sm:items-center bg-black/60 backdrop-blur-xl">
              <div className="bg-black/80 w-full sm:w-[90%] md:w-[600px] rounded-t-[40px] sm:rounded-3xl border border-white/10 shadow-2xl p-8 pb-12 animate-[slideUp_0.4s_ease-out] relative overflow-hidden flex flex-col max-h-[90vh]">
                  <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-gold/10 rounded-full blur-[100px] pointer-events-none" />
                  <div className="relative z-10 overflow-y-auto pr-2 custom-scrollbar">
                      <div className="flex justify-between items-start mb-6">
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-900 to-black flex items-center justify-center text-gold border border-gold/40">
                              {React.cloneElement(selectedCourse.icon, { size: 32 })}
                          </div>
                          <button onClick={() => setSelectedCourse(null)} className="p-3 bg-white/5 rounded-full text-gray-400 hover:rotate-90 transition-all"><X size={24} /></button>
                      </div>
                      <h2 className="text-3xl font-extrabold text-white mb-3">{selectedCourse.title}</h2>
                      
                      {selectedCourse.video && (
                        <div className="mb-6 aspect-video w-full rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-black">
                             <iframe width="100%" height="100%" src={selectedCourse.video} title="Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                        </div>
                      )}

                      <p className="text-gray-300 text-base mb-6 font-light leading-relaxed">{selectedCourse.fullDesc}</p>
                      
                      {selectedCourse.homework && (
                        <div className="mb-8 p-5 bg-indigo-500/10 border border-indigo-500/20 rounded-2xl relative overflow-hidden group/hw">
                            <div className="absolute top-0 right-0 p-2 opacity-20 group-hover/hw:opacity-100 transition-opacity"><PenTool size={16} /></div>
                            <h4 className="text-indigo-400 font-bold text-xs uppercase tracking-widest mb-2">Mājasdarbs</h4>
                            <p className="text-xs text-gray-200 italic">{selectedCourse.homework}</p>
                            <p className="text-[10px] text-gray-500 mt-2">Iesūti risinājumu botā, rakstot: <span className="text-white font-mono">/darbs [tavs teksts]</span></p>
                        </div>
                      )}
                      <div className="flex gap-4 mb-6 text-sm font-bold">
                          <div className="flex items-center gap-2 bg-white/5 py-2 px-4 rounded-xl border border-white/5"><Clock size={16} className="text-gold"/> {selectedCourse.time}</div>
                          <div className="flex items-center gap-2 bg-white/5 py-2 px-4 rounded-xl border border-white/5"><BarChart size={16} className="text-gold"/> {selectedCourse.level}</div>
                          <button 
                            onClick={() => {
                                const title = encodeURIComponent(selectedCourse.title);
                                const desc = encodeURIComponent(`Lektors AI Nodarbība: ${selectedCourse.desc}`);
                                const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&details=${desc}&sf=true&output=xml`;
                                window.open(url, '_blank');
                            }}
                            className="flex items-center gap-2 bg-indigo-500/10 hover:bg-indigo-500/20 py-2 px-4 rounded-xl border border-indigo-500/20 text-indigo-400 transition-all"
                          >
                            <Calendar size={16} /> <span className="text-[10px]">PLĀNOT</span>
                          </button>
                      </div>
                      <button onClick={() => { playSound('click'); handleStartLesson(selectedCourse.id); }} className="w-full bg-gold text-black font-extrabold py-5 rounded-2xl hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all flex items-center justify-center gap-3 text-xl">SĀKT LEKCIJU <Rocket size={24} /></button>
                  </div>
              </div>
          </div>
        )}
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(212, 175, 55, 0.2); border-radius: 10px; }
      `}} />
    </div>
  );
};

export default StudentApp;
